import React, { useState } from 'react';

import isEqual from 'lodash/isEqual';

import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Chip, Divider, Paper, Stack, Typography } from '@mui/material';

interface Props {
  previous: Record<string, unknown> | null;
  proposed: Record<string, unknown>;
  compact?: boolean;
}

type ChangedField = { key: string; prev: unknown; next: unknown };

type DisplayChange = ChangedField & {
  label: string;
  summary: string;
  isLongText: boolean;
  oldDisplay: React.ReactNode;
  newDisplay: React.ReactNode;
};

const IMAGE_TRANSFORM = 'fl_progressive,q_auto:eco,c_fill,w_220,h_140';
const LONG_TEXT_LENGTH = 80;
const PREVIOUS_VALUE_KEY_ALIASES: Record<string, string> = {
  gallery: 'images',
};

export function getChangedFields(
  previous: Record<string, unknown> | null,
  proposed: Record<string, unknown>,
): ChangedField[] {
  return Object.keys(proposed)
    .map((key) => {
      const previousKey = previous && key in previous ? key : PREVIOUS_VALUE_KEY_ALIASES[key];
      return { key, prev: previousKey ? previous?.[previousKey] : undefined, next: proposed[key] };
    })
    .filter(({ prev, next }) => !isEqual(prev, next));
}

function humanizeKey(key: string): string {
  const explicitLabels: Record<string, string> = {
    all_taxes_and_fees_as_non_commssionable: 'All taxes and fees non-commissionable',
    address: 'Street Address',
    category: 'Property Type / Category',
    commissionable_taxes_and_fees: 'Commissionable taxes and fees',
    include_tax_amount_into_before_amount: 'Include tax amount in before amount',
    location_heading: 'Location City',
    location_subheading: 'Location Country/State',
    logo_id_cloudinary_external: 'Logo',
    name: 'Property Name',
    taxes_and_fees: 'Taxes and fees',
    taxes_and_fees_content: 'Taxes and fees content',
    taxes_payable_at_property: 'Taxes payable at property',
  };

  if (explicitLabels[key]) return explicitLabels[key];

  return key
    .replace(/_id_cloudinary_external$/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function itemIdentity(value: unknown): string {
  if (isRecord(value)) {
    const identity =
      value.id_cloudinary_external ??
      value.publicImageId ??
      value.imageId ??
      value.idCloudinaryExternal ??
      value.name ??
      value.facilityCode ??
      value.content ??
      value.id;
    if (typeof identity === 'string' || typeof identity === 'number') return String(identity);
  }

  return JSON.stringify(value);
}

function formatFacilityCode(code: unknown): string {
  if (typeof code !== 'string') return 'Facility';

  return code
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatBedType(type: string): string {
  return type
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function getBedGroupItems(value: Record<string, unknown>): string[] {
  if (typeof value.description === 'string' && value.description.trim()) return [value.description.trim()];
  if (!Array.isArray(value.configuration)) return [];

  return value.configuration
    .map((item) => {
      if (!isRecord(item)) return undefined;

      const type = typeof item.type === 'string' ? formatBedType(item.type) : '';
      const quantity = typeof item.quantity === 'number' ? item.quantity : undefined;

      if (!type || quantity === undefined) return undefined;

      return `${quantity} ${type}`;
    })
    .filter(Boolean);
}

function formatBedGroup(value: Record<string, unknown>): string | undefined {
  const configuration = getBedGroupItems(value);

  return configuration.length > 0 ? configuration.join(', ') : undefined;
}

function formatSimpleValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'Not set';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? '' : 's'}`;

  if (isRecord(value)) {
    if (typeof value.amount === 'number')
      return `${value.amount}${typeof value.currency === 'string' ? ` ${value.currency}` : ''}`;
    if (typeof value.value === 'number') return `${value.value}%`;
    if (typeof value.title === 'string') return value.title;
    if (typeof value.content === 'string') return value.content;
    if (typeof value.name === 'string') return value.name;
  }

  return 'Changed';
}

function isUnsetValue(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

function isLongTextValue(value: unknown): boolean {
  return typeof value === 'string' && (value.length >= LONG_TEXT_LENGTH || value.includes('\n'));
}

function isLongTextChange(prev: unknown, next: unknown): boolean {
  return isLongTextValue(prev) || isLongTextValue(next);
}

function getChangeStatus(prev: unknown, next: unknown): string {
  if (isUnsetValue(prev) && !isUnsetValue(next)) return 'Added';
  if (!isUnsetValue(prev) && isUnsetValue(next)) return 'Removed';

  return 'Updated';
}

function getProseStatus(prev: unknown, summary: string): string {
  return isUnsetValue(prev) ? `${formatSimpleValue(prev)} -> ${summary}` : summary;
}

function formatItem(value: unknown): string {
  if (isRecord(value)) {
    if (typeof value.facilityCode === 'string') return formatFacilityCode(value.facilityCode);

    const bedGroup = formatBedGroup(value);
    if (bedGroup) return bedGroup;

    const parts = [
      typeof value.name === 'string' ? value.name.trim() : undefined,
      value.unit ? formatSimpleValue(value.unit) : undefined,
      value.payable_at_property === true ? 'payable at property' : undefined,
      value.commissionable_type === 'non-commissionable' ? 'non-commissionable' : undefined,
    ].filter(Boolean);

    if (parts.length > 0) return parts.join(' - ');
  }

  return formatSimpleValue(value);
}

function isFeaturedFacility(value: unknown): boolean {
  return isRecord(value) && typeof value.facilityCode === 'string' && value.featured === true;
}

function isBedGroupItem(value: unknown): value is Record<string, unknown> {
  return isRecord(value) && getBedGroupItems(value).length > 0;
}

function getImageHost(): string | undefined {
  return window.configs?.IMAGE_HOST;
}

function isAbsoluteImageUrl(value: string): boolean {
  return /^https?:\/\//.test(value);
}

function buildImageUrl(imageIdOrUrl: string): string | undefined {
  if (isAbsoluteImageUrl(imageIdOrUrl)) return imageIdOrUrl;

  const host = getImageHost();
  if (!host) return undefined;

  return `${host}/${IMAGE_TRANSFORM}/${imageIdOrUrl}`;
}

function getImageId(value: unknown, allowStringImageId = false): string | undefined {
  if (allowStringImageId && typeof value === 'string' && value.trim()) return value;

  if (!isRecord(value)) return undefined;

  const imageId =
    value.id_cloudinary_external ??
    value.logo_id_cloudinary_external ??
    value.publicImageId ??
    value.imageId ??
    value.idCloudinaryExternal ??
    value.cloudinaryId ??
    value.url ??
    value.imageUrl;

  return typeof imageId === 'string' && imageId.trim() ? imageId : undefined;
}

function getImageTitle(value: unknown, fallback: string): string {
  if (!isRecord(value)) return fallback;

  const title = value.title ?? value.name ?? value.filename;
  return typeof title === 'string' && title.trim() ? title.trim() : fallback;
}

function getImagePreview(value: unknown, alt: string, allowStringImageId = false): React.ReactNode {
  const imageId = getImageId(value, allowStringImageId);
  const imageUrl = imageId ? buildImageUrl(imageId) : undefined;

  if (!imageUrl) return null;

  return (
    <Box
      component="img"
      src={imageUrl}
      alt={alt}
      sx={{
        aspectRatio: '11 / 7',
        bgcolor: 'grey.200',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        display: 'block',
        objectFit: 'cover',
        width: '100%',
      }}
    />
  );
}

function getArraySummary(prev: unknown[], next: unknown[]): string {
  if (prev.length === 0 && next.length === 0) return 'No items';

  const previousByIdentity = new Map(prev.map((item) => [itemIdentity(item), item]));
  const nextByIdentity = new Map(next.map((item) => [itemIdentity(item), item]));

  const added = next.filter((item) => !previousByIdentity.has(itemIdentity(item)));
  const removed = prev.filter((item) => !nextByIdentity.has(itemIdentity(item)));
  const changed = next.filter((item) => {
    const previousItem = previousByIdentity.get(itemIdentity(item));
    return previousItem !== undefined && !isEqual(previousItem, item);
  });

  const summaryParts = [
    added.length > 0 ? `${added.length} added` : undefined,
    removed.length > 0 ? `${removed.length} removed` : undefined,
    changed.length > 0 ? `${changed.length} changed` : undefined,
  ].filter(Boolean);

  if (next.some((item) => isRecord(item) && item.featured === true)) {
    const featuredCount = next.filter((item) => isRecord(item) && item.featured === true).length;
    summaryParts.push(`${featuredCount} featured`);
  }

  return summaryParts.join(', ') || `${prev.length} -> ${next.length} items`;
}

function getSummary(key: string, prev: unknown, next: unknown): string {
  if (key === 'logo_id_cloudinary_external') return 'Logo changed';
  if (isLongTextChange(prev, next)) return getChangeStatus(prev, next);

  if (Array.isArray(prev) || Array.isArray(next)) {
    return getArraySummary(Array.isArray(prev) ? prev : [], Array.isArray(next) ? next : []);
  }

  return `${formatSimpleValue(prev)} -> ${formatSimpleValue(next)}`;
}

function renderList(value: unknown, label: string): React.ReactNode {
  if (!Array.isArray(value)) return null;
  const imageAltPrefix = label.split(' ')[0] ?? label;

  if (value.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        None
      </Typography>
    );
  }

  return (
    <Stack component="ul" spacing={1} sx={{ m: 0, pl: 0, listStyle: 'none' }}>
      {value.map((item, index) => (
        <Box
          key={`${itemIdentity(item)}-${index}`}
          component="li"
          sx={
            isFeaturedFacility(item)
              ? {
                  bgcolor: 'warning.50',
                  border: '1px solid',
                  borderColor: 'warning.light',
                  borderRadius: 1,
                  px: 1,
                  py: 0.75,
                }
              : undefined
          }
        >
          <Stack spacing={0.75}>
            {getImagePreview(item, `${imageAltPrefix} ${getImageTitle(item, `Image ${index + 1}`)}`)}
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
              <Typography variant="body2">{formatItem(item)}</Typography>
              {isFeaturedFacility(item) && <Chip size="small" color="warning" variant="outlined" label="Featured" />}
            </Stack>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

function renderBedGroupList(value: unknown): React.ReactNode {
  if (!Array.isArray(value)) return null;

  if (value.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        None
      </Typography>
    );
  }

  return (
    <Stack component="ul" spacing={1} sx={{ m: 0, pl: 0, listStyle: 'none' }}>
      {value.map((item, index) => (
        <Box
          key={`${itemIdentity(item)}-${index}`}
          component="li"
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1,
          }}
        >
          <Stack spacing={0.75}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              {`Bed group ${index + 1}`}
            </Typography>
            <Stack spacing={0.5}>
              {isBedGroupItem(item) ? (
                getBedGroupItems(item).map((bed, bedIndex) => (
                  <Typography key={`${bed}-${bedIndex}`} variant="body2">
                    {bed}
                  </Typography>
                ))
              ) : (
                <Typography variant="body2">{formatItem(item)}</Typography>
              )}
            </Stack>
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

function isStandaloneImageField(key: string): boolean {
  return key === 'logo_id_cloudinary_external';
}

function renderDisplayValue(value: unknown, key: string, label: string, allowStringImageId = false): React.ReactNode {
  if (key === 'bed_groups' && Array.isArray(value)) return renderBedGroupList(value);
  if (Array.isArray(value)) return renderList(value, label);

  const imagePreview = getImagePreview(value, label, allowStringImageId);

  return (
    <Stack spacing={0.75}>
      {imagePreview}
      <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
        {formatSimpleValue(value)}
      </Typography>
    </Stack>
  );
}

function createDisplayChange({ key, prev, next }: ChangedField): DisplayChange {
  const label = humanizeKey(key);
  const allowStringImageId = isStandaloneImageField(key);
  const isLongText = isLongTextChange(prev, next);

  return {
    key,
    prev,
    next,
    label,
    summary: getSummary(key, prev, next),
    isLongText,
    oldDisplay: renderDisplayValue(prev, key, `Before ${label}`, allowStringImageId),
    newDisplay: renderDisplayValue(next, key, `After ${label}`, allowStringImageId),
  };
}

function CompactChange({ change }: { change: DisplayChange }) {
  return (
    <Stack spacing={0.25} sx={{ mb: 0.75, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        {change.label}
      </Typography>
      <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
        {change.summary}
      </Typography>
    </Stack>
  );
}

function ValuePanel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.25, flex: 1, minWidth: 220, bgcolor: 'grey.50' }}>
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        {label}
      </Typography>
      <Box sx={{ mt: 0.75 }}>{children}</Box>
    </Paper>
  );
}

function ProseValuePanel({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        bgcolor: 'grey.50',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        {label}
      </Typography>
      <Box
        sx={{
          mt: 0.75,
          '& .MuiTypography-body2': {
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
          },
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

function getFullSummary(change: DisplayChange): string | undefined {
  if (Array.isArray(change.prev) || Array.isArray(change.next)) return change.summary;

  return undefined;
}

function FullChangeCard({ change, showPrevious }: { change: DisplayChange; showPrevious: boolean }) {
  if (change.isLongText) {
    return (
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
        <Stack spacing={1.25}>
          <Stack spacing={0.5}>
            <Typography variant="subtitle2" fontWeight={700}>
              {change.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              {showPrevious ? getProseStatus(change.prev, change.summary) : change.summary}
            </Typography>
          </Stack>
          {showPrevious && <ProseValuePanel label="Before">{change.oldDisplay}</ProseValuePanel>}
          <ProseValuePanel label="After">{change.newDisplay}</ProseValuePanel>
        </Stack>
      </Paper>
    );
  }

  const fullSummary = getFullSummary(change);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
      <Stack spacing={1.25}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle2" fontWeight={700}>
            {change.label}
          </Typography>
          {fullSummary && (
            <Typography variant="caption" color="text.secondary" fontWeight={700}>
              {fullSummary}
            </Typography>
          )}
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} alignItems="stretch">
          {showPrevious && <ValuePanel label="Before">{change.oldDisplay}</ValuePanel>}
          {showPrevious && <ArrowForwardIcon sx={{ alignSelf: 'center', color: 'text.disabled' }} />}
          <ValuePanel label="After">{change.newDisplay}</ValuePanel>
        </Stack>
      </Stack>
    </Paper>
  );
}

function TechnicalDetails({
  previous,
  proposed,
}: {
  previous: Record<string, unknown> | null;
  proposed: Record<string, unknown>;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box>
      <Button
        size="small"
        variant="text"
        startIcon={<ExpandMoreIcon sx={{ transform: isOpen ? 'rotate(180deg)' : undefined }} />}
        onClick={() => setIsOpen((current) => !current)}
      >
        {isOpen ? 'Hide technical details' : 'View technical details'}
      </Button>
      {isOpen && (
        <Box
          data-testid="technical-details"
          component="pre"
          sx={{
            bgcolor: 'grey.100',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            fontSize: 12,
            mt: 1,
            overflow: 'auto',
            p: 1.5,
            whiteSpace: 'pre-wrap',
          }}
        >
          {JSON.stringify({ previous, proposed }, null, 2)}
        </Box>
      )}
    </Box>
  );
}

export default function ValueDiff({ previous, proposed, compact }: Props) {
  const changed = getChangedFields(previous, proposed);

  if (changed.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No field changes detected
      </Typography>
    );
  }

  const displayChanges = changed.map(createDisplayChange);

  if (compact) {
    return (
      <>
        {displayChanges.map((change) => (
          <CompactChange key={change.key} change={change} />
        ))}
      </>
    );
  }

  return (
    <Stack spacing={1.5}>
      {displayChanges.map((change) => (
        <FullChangeCard key={change.key} change={change} showPrevious={previous !== null} />
      ))}
      <Divider />
      <TechnicalDetails previous={previous} proposed={proposed} />
    </Stack>
  );
}
